import { keepPreviousData, staleTimes, useQuery } from '@platform/api-client';
import { browserTimeZone } from '@platform/design-system';
import type { Operation, QueryParams, ResponseBody } from '@platform/openapi';
import { dayRangePeriodOf, type SeriesUnit } from '../../report-period.ts';
import { queryKeys, usePageContext } from '../../usePageContext.ts';
import {
  comparisonQueryOf,
  performanceQueryOf,
  productFilterQueryOf,
  type TopProductsRequest,
} from '../performance.ts';
import type { WidgetContext } from './widget-types.ts';

export type BreakdownReport = ResponseBody<Operation<'/admin/dashboard/breakdown', 'get'>>;
export type BreakdownDimension = BreakdownReport['dimension'];
export type BreakdownMeasure = BreakdownReport['measure'];
export type FunnelReport = ResponseBody<Operation<'/admin/dashboard/funnel', 'get'>>;

type BreakdownQuery = NonNullable<QueryParams<Operation<'/admin/dashboard/breakdown', 'get'>>>;

export const salesFilterQueryOf = ({
  company,
  productFilter,
}: Pick<WidgetContext, 'company' | 'productFilter'>) => ({
  ...(company === null ? {} : { company }),
  ...productFilterQueryOf(productFilter),
});

export type PerformanceNeeds = {
  readonly unit: SeriesUnit;
  readonly topProducts?: TopProductsRequest;
  readonly enabled?: boolean;
};

export const usePerformanceFor = (
  context: WidgetContext,
  { unit, topProducts, enabled = true }: PerformanceNeeds,
) => {
  const { tenantId } = usePageContext();
  return useQuery({
    ...performanceQueryOf({
      api: context.api,
      tenantId,
      period: dayRangePeriodOf(context.range),
      unit,
      company: context.company,
      productFilter: context.productFilter,
      comparison: context.comparison,
      ...(topProducts === undefined ? {} : { topProducts }),
    }),
    placeholderData: keepPreviousData,
    enabled,
  });
};

export type BreakdownRequest = {
  readonly dimension: BreakdownDimension;
  readonly measure: BreakdownMeasure;
  readonly unit: SeriesUnit;
};

export const useBreakdown = (context: WidgetContext, request: BreakdownRequest) => {
  const { tenantId } = usePageContext();
  const query: BreakdownQuery = {
    ...dayRangePeriodOf(context.range),
    ...request,
    timeZone: browserTimeZone(),
    ...salesFilterQueryOf(context),
    ...comparisonQueryOf(context.comparison),
  };
  return useQuery({
    queryKey: [...queryKeys.dashboard(tenantId), 'breakdown', query],
    queryFn: () => context.api.get('/admin/dashboard/breakdown', { query }),
    staleTime: staleTimes.dashboard,
    placeholderData: keepPreviousData,
  });
};

export const useFunnel = (context: WidgetContext) => {
  const { tenantId } = usePageContext();
  const query = {
    ...dayRangePeriodOf(context.range),
    timeZone: browserTimeZone(),
    ...salesFilterQueryOf(context),
    ...comparisonQueryOf(context.comparison),
  };
  return useQuery({
    queryKey: [...queryKeys.dashboard(tenantId), 'funnel', query],
    queryFn: () => context.api.get('/admin/dashboard/funnel', { query }),
    staleTime: staleTimes.dashboard,
    placeholderData: keepPreviousData,
  });
};
